// @flow
import type {
  Customer,
  Address,
  AddressRaw,
  AddressGeo,
  Invoice,
  Review,
  DeliveryMethodName,
  InputType,
  InvoiceTax,
  TaxInvoiceStateType,
  OrderTaxInvoiceType,
  Country,
} from '.';

import type {TaxInvoiceTypeName} from '.';

// export type TaxInvoiceTypeName = 'BOLETA' | 'FACTURA' | 'NOTA_CREDITO';

export const ORDER_STATES = [
  'PENDING',
  'CONFIRMED',
  'PAID',
  'ON_THE_WAY',
  'DELIVERED',
  'CANCELLED',
];

export const ORDER_STATE_STRINGS = Object.freeze({
  PENDING: 'Pendiente',
  CONFIRMED: 'Aceptado',
  PAID: 'Pagado',
  ON_THE_WAY: 'En ruta',
  DELIVERED: 'Entregado',
  CANCELLED: 'Cancelado',
});

export const ORDER_STATE_COLORS = Object.freeze({
  PENDING: {main: '#FDAC42', light: 'rgba(253, 172, 66, 0.15)'},
  CONFIRMED: {main: '#FF7337', light: '#FFF1EB'},
  PAID: {main: '#00B382', light: '#E6F7F3'},
  ON_THE_WAY: {main: '#C355DF', light: '#F2E1F7'},
  DELIVERED: {main: '#158CFD', light: '#EDF6FE'},
  CANCELLED: {main: '#E13065', light: '#FDECF1'},
});

export const ORDER_STATE_CHANGE_DESCRIPTIONS = Object.freeze({
  PENDING: '',
  CONFIRMED:
    'Acabas de recibir una orden pendiente que todavía no está pagada.\nConfírmale a tu cliente que la recibiste y envíale las posibles formas de pago.',
  PAID: '¡Felicidades! Recibiste un pago. ',
  ON_THE_WAY: 'La orden se encuentra en proceso de envío',
  DELIVERED: 'La orden es recepcionada por el cliente',
  CANCELLED: 'La orden es cancelada',
});

export const ORDER_STATE_CHANGE_MESSAGE_PLACEHOLDERS = Object.freeze({
  PENDING:
    '¡Hola! {{nombre_cliente}}, te escribimos de (Marca). Muchas gracias por confiar en nosotros.\nHemos recibido tu pedido, y contamos con los siguientes métodos de pago: Yape, transferencia Bancaria, Mercado Pago (pagos con tarjeta).\n\nPuedes realizar el pago y adjuntar comprobante en el siguiente link: {{link_de_pago}}',
  CONFIRMED:
    '¡Hola! {{nombre_cliente}}, te escribimos de (Marca). Muchas gracias por confiar en nosotros.\nHemos recibido tu pedido, y contamos con los siguientes métodos de pago: Yape, transferencia Bancaria, Mercado Pago (pagos con tarjeta).\n\nPuedes realizar el pago y adjuntar comprobante en el siguiente link: {{link_de_pago}}',
  PAID:
    '¡Hola! {{nombre_cliente}}, te escribimos de (Marca). Muchas gracias por confiar en nosotros.\nHemos recibido el pago de tu orden y estaremos gestionando el envío el día de mañana.\n\nMuchas gracias.',
  ON_THE_WAY:
    '¡Hola! {{nombre_cliente}}, te escribimos de (Marca). Muchas gracias por confiar en nosotros.\nHemos recibido el pago de tu orden y estaremos gestionando el envío el día de mañana.\n\nMuchas gracias.',
  DELIVERED:
    '¡Hola! {{nombre_cliente}}, te escribimos de (Marca). Esperamos que disfrutes tu pedido.\nSi todo está de tu agrado, te agradeceríamos que nos dejes una reseña de tu experiencia de compra en el siguiente enlace:\n\nLink de reseña',
  CANCELLED:
    '¡Hola! {{nombre_cliente}}, te escribimos de (Marca). Tu pedido ha sido cancelado. Esperamos que pronto vuelvas a comprar con nosotros.\n\n(Link de tienda)',
});

export const ORDER_STATE_FILTER_TOOLTIPS = Object.freeze({
  PENDING: 'Pedido creado',
  CONFIRMED: 'Pedido revisado sin pago efectuado',
  PAID: 'Pedido fue pagado, se realiza descuento del inventario',
  ON_THE_WAY: 'Pedido programado para su envío',
  DELIVERED: 'Pedido recepcionado por cliente',
  CANCELLED: 'Pedido no válido',
});

export type OrderState =
  | 'PENDING'
  | 'CONFIRMED'
  | 'PAID'
  | 'ON_THE_WAY'
  | 'DELIVERED'
  | 'CANCELLED';

export type OrderCreationType = 'DEFAULT' | 'MANUAL' | 'PRE_ORDER';

type OrderItemProductOption = {
  id: string,
  imageId?: string,
  name: string,
  price: string,
  productOptionGroupId: string,
  quantity: number,
  text?: string,
};
type OrderItemDetails = {
  product: {
    id: string,
    imageId: string,
    name: string,
    price: string,
  },
  productOptions: OrderItemProductOption[],
  product_variant?: {
    id: string,
    imageId: string,
    price: string,
    combinations: {
      [key: string]: string,
    },
  },
};

export type OrderItem = {
  comment: ?string,
  id: string,
  name: string,
  price: string,
  imageId: string,
  quantity: number,
  productId: string,
  productVariant?: {[key: string]: string},
  productVariantId?: string,
  type: 'order-item' | 'pre-order-item',
  detail: OrderItemDetails,
  // pre-order fields
  preOrderItemPriceType?: 'OFFER_PRICE' | 'REGULAR_PRICE',
  originalPrice?: string,
};

export type Voucher = {
  createdAt: number,
  fileId?: string,
  state: string,
  type: 'order-voucher-file',
  updatedAt: number,
};

export type OrderAdditionalInformation = {
  additionalInformation: {
    value: string | Array<string>,
    number: number,
  },
  storeOrderAdditionalInformation: {
    id: string,
    enabled: boolean,
    required: boolean,
    name: string,
    number: number,
    schema?: {[key: string]: any},
    description?: string,
    inputType: InputType,
  },
};

export type AdditionalInformation = OrderAdditionalInformation[];

export type PreOrderPromotion = {
  discount: string,
  name: string,
  type: 'pre-order-promotion',
};

export type PreOrderShippingPromotion = {
  discount: string,
  name: string,
  percentage: string,
};

export type ShippingDetail = {
  shippingAddressGeo?: AddressGeo,
  shippingAddressRaw?: AddressRaw,
  shippingAddressAdmin?: Address,
  shippingAddressType: 'GEO' | 'RAW' | 'ADMIN',
  shippingMethodOption: string,
  flatRateLabel?: string,
  flatRateId?: string,
};
export type OrderComment = {
  id: string,
  comment: string,
  userId: string,
  userName: string,
  userType: string,
  createdAt: number,
  type: 'order-comment',
};

export type PreOrderGlobalDiscount = {
  description: 'FIXED_DISCOUNT' | 'PERCENTAGE_DISCOUNT',
  discount: string,
  type: 'pre-order-global-discount',
};

export type PreOrderCoupon = {
  code: string,
  couponScopeType: 'ORDER',
  id: string,
  orderDiscount: string,
  productDiscounts: any[],
  totalDiscount: string,
  type: 'pre-order-coupon',
};

export type ShippingAddress = {
  address1: string,
  address2: string,
  admin1?: {
    admin1Id: number,
    admin1Name: string,
    type: 'admin1',
  },
  admin2?: {
    admin2Id: number,
    admin2Name: string,
    type: 'admin2',
  },
  admin3?: {
    admin3Id: number,
    admin3Name: string,
    type: 'admin3',
  },
  country?: Country,
  id: string,
  type: 'shipping-address',
  zipCode?: string,
};

export type OrderTaxInvoiceData = {
  amount: string,
  country: {
    code: string,
    code3: string,
    id: string,
    name: string,
    phonePrefix: string,
    type: string,
  },
  createdAt: number,
  id: string,
  issueDate?: number,
  orderId: string,
  pdfUrl?: string,
  relatedTaxInvoiceId?: string,
  state: TaxInvoiceStateType,
  storeId: string,
  taxInvoiceProvider: {
    country: {
      code: string,
      code3: string,
      id: string,
      name: string,
      phonePrefix: string,
      type: string,
    },
    currency: {
      code: string,
      id: string,
      name: string,
      symbol: string,
      type: string,
    },
    id: string,
    label: string,
    name: string,
    type: string,
  },
  taxInvoiceProviderId: string,
  taxInvoiceType: OrderTaxInvoiceType,
  taxInvoiceTypeId: string,
  taxReceiverCode: string,
  taxReceiverName: string,
  taxTransmitterCode: string,
  taxTransmitterName: string,
  type: string,
  updatedAt: number,
  formattedExternalReferenceId?: string,
};

export type OrderTaxInvoiceInformation = {
  taxReceiverCode: string,
  taxReceiverName: string,
  address?: string,
  admin1?: {
    admin1Id: number,
    admin1Name: string,
    type: 'admin1',
  },
  admin2?: {
    admin2Id: number,
    admin2Name: string,
    type: 'admin2',
  },
  admin3?: {
    admin3Id: number,
    admin3Name: string,
    type: 'admin3',
  },
  taxInvoiceType: {
    label: string,
    name: TaxInvoiceTypeName,
    id: string,
  },
  id: string,
};

type PaymentInvoice = {
  taxes: InvoiceTax[],
};

export type OrderTaxInvoice = {
  taxInvoiceList: OrderTaxInvoiceData[],
  taxInvoiceInformation?: OrderTaxInvoiceInformation,
};

type InvoiceReceipt = {
  createdAt: number,
  id: string,
  type: 'invoice-receipt-file',
  updatedAt: number,
};
export type Order = {
  createdAt: number,
  customer: Customer,
  shippingDetail?: {
    flatRateLabel: string,
    option: string,
  },
  deliveryMethod: DeliveryMethodName,
  id: string,
  invoice: Invoice,
  items: OrderItem[],
  shippingDetail: ShippingDetail,
  orderState: OrderState,
  token: string,
  updatedAt: number,
  orderReview?: Review,
  voucher: Voucher,
  additionalInformation?: AdditionalInformation,
  availableOrderStatesToProceed: OrderState[],
  availableOrderStatesToRevert: OrderState[],
  orderCreationType: OrderCreationType,
  type: 'order' | 'pre-order',
  collaboratorId: string,
  shippingAddress?: ShippingAddress,

  // fake auxiliary fields
  previousState?: OrderState,

  // pre-order fields
  preOrderState?: string,
  subTotal?: string,
  total?: string,
  promotions?: PreOrderPromotion[],
  shippingPromotion?: PreOrderShippingPromotion,
  order?: Order,
  globalDiscount?: PreOrderGlobalDiscount,
  coupon?: PreOrderCoupon,

  receipts?: InvoiceReceipt[],

  taxInvoiceData?: OrderTaxInvoice,
  taxes?: InvoiceTax[],
  paymentInvoice: PaymentInvoice,
};
